import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { FadeModal } from '@/components/ui/fade-modal'
import { AddPropertyToCollectionModal } from '@/components/collections/AddPropertyToCollectionModal'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import {
  deleteCollection,
  getCollection,
  listCollectionItems,
  NOTE_MAX,
  removePropertyFromCollection,
  renameCollection,
  updateCollectionItemNote,
} from '@/lib/collections'
import { callableErrorMessage } from '@/lib/callable-errors'
import { openPropertyProfile } from '@/lib/property-navigation'
import type { CollectionItem, PropertyCollection } from '@/types/models'

export function CollectionDetailPage() {
  return (
    <RequireAuth>
      <CollectionDetail />
    </RequireAuth>
  )
}

function CollectionDetail() {
  const { collectionId = '' } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [collection, setCollection] = useState<PropertyCollection | null>(null)
  const [items, setItems] = useState<CollectionItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [noteItem, setNoteItem] = useState<CollectionItem | null>(null)
  const [removeItem, setRemoveItem] = useState<CollectionItem | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  async function reload() {
    if (!user || !collectionId) return
    setLoading(true)
    setError(null)
    try {
      const col = await getCollection(user.uid, collectionId)
      if (!col) {
        setCollection(null)
        setItems([])
        setError('Collection not found.')
        return
      }
      const list = await listCollectionItems(user.uid, collectionId)
      setCollection(col)
      setItems(list)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, collectionId])

  async function onRemoveConfirmed() {
    if (!user || !collectionId || !removeItem) return
    const propertyId = removeItem.propertyId
    setBusyId(propertyId)
    try {
      await removePropertyFromCollection(user.uid, collectionId, propertyId)
      setItems((prev) => prev.filter((i) => i.propertyId !== propertyId))
      setCollection((c) => (c ? { ...c, itemCount: Math.max(0, c.itemCount - 1) } : c))
      setRemoveItem(null)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusyId(null)
    }
  }

  async function onDeleteCollection() {
    if (!user || !collection || collection.isFavourites) return
    const ok = window.confirm(`Delete “${collection.name}”? This can’t be undone.`)
    if (!ok) return
    try {
      await deleteCollection(user.uid, collection.id)
      navigate('/collections', { replace: true })
    } catch (e) {
      setError(callableErrorMessage(e))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (!collection) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-danger">{error ?? 'Collection not found.'}</p>
        <Button asChild variant="outline">
          <Link to="/collections">Back to Collections</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-1">
            <Link to="/collections">← Collections</Link>
          </Button>
          <h1 className="text-2xl font-bold text-navy">{collection.name}</h1>
          {collection.description ? (
            <p className="mt-1 text-sm text-muted">{collection.description}</p>
          ) : null}
          <p className="mt-1 text-sm text-muted">
            {collection.itemCount} {collection.itemCount === 1 ? 'property' : 'properties'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="primary" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" />
            Add property
          </Button>
          {!collection.isFavourites ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => void onDeleteCollection()}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {items.length === 0 ? (
        <EmptyState
          title="No properties yet"
          description="Add a property here, or save one from Search or Map."
          actionLabel="Add property"
          onAction={() => setAddOpen(true)}
        />
      ) : (
        <ul className="space-y-3">
          {items.map((item) => {
            const { street, locality } = splitAddressDisplay(item.fullAddress)
            return (
              <li key={item.propertyId}>
                <Card className="transition hover:border-biddle-blue">
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1 space-y-3">
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() =>
                            openPropertyProfile(navigate, item.propertyId, {
                              pathname: location.pathname,
                            })
                          }
                        >
                          <p className="font-semibold text-navy">{street || 'Property'}</p>
                          {locality ? (
                            <p className="mt-0.5 text-sm text-muted">{locality}</p>
                          ) : null}
                        </button>
                        {item.note ? (
                          <p className="rounded-xl bg-surface px-3 py-2 text-sm text-charcoal">
                            {item.note}
                          </p>
                        ) : null}
                      </div>
                      <ItemOverflowMenu
                        open={menuOpenId === item.propertyId}
                        onOpenChange={(open) =>
                          setMenuOpenId(open ? item.propertyId : null)
                        }
                        noteLabel={item.note ? 'Edit note' : 'Add note'}
                        onEditNote={() => {
                          setMenuOpenId(null)
                          setNoteItem(item)
                        }}
                        onRemove={() => {
                          setMenuOpenId(null)
                          setRemoveItem(item)
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </li>
            )
          })}
        </ul>
      )}

      <AddPropertyToCollectionModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        uid={user!.uid}
        collectionId={collection.id}
        existingPropertyIds={new Set(items.map((i) => i.propertyId))}
        onAdded={({ propertyId, fullAddress }) => {
          const now = new Date()
          setItems((prev) => {
            if (prev.some((i) => i.propertyId === propertyId)) return prev
            return [
              {
                propertyId,
                fullAddress,
                note: '',
                addedAt: now,
                updatedAt: now,
              },
              ...prev,
            ]
          })
          setCollection((c) =>
            c ? { ...c, itemCount: c.itemCount + 1, updatedAt: now } : c,
          )
        }}
      />

      <EditCollectionModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        collection={collection}
        uid={user!.uid}
        onSaved={(next) => {
          setCollection(next)
          setEditOpen(false)
        }}
      />

      <EditNoteModal
        open={Boolean(noteItem)}
        item={noteItem}
        onClose={() => setNoteItem(null)}
        uid={user!.uid}
        collectionId={collection.id}
        onSaved={(propertyId, note) => {
          setItems((prev) =>
            prev.map((i) => (i.propertyId === propertyId ? { ...i, note } : i)),
          )
          setNoteItem(null)
        }}
      />

      <RemovePropertyConfirmModal
        open={Boolean(removeItem)}
        item={removeItem}
        busy={Boolean(removeItem && busyId === removeItem.propertyId)}
        onClose={() => {
          if (busyId) return
          setRemoveItem(null)
        }}
        onConfirm={() => void onRemoveConfirmed()}
      />
    </div>
  )
}

function ItemOverflowMenu({
  open,
  onOpenChange,
  noteLabel,
  onEditNote,
  onRemove,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  noteLabel: string
  onEditNote: () => void
  onRemove: () => void
}) {
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      const root = rootRef.current
      if (!root) return
      if (e.target instanceof Node && !root.contains(e.target)) {
        onOpenChange(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onOpenChange])

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className="rounded-lg p-1.5 text-muted transition hover:bg-surface hover:text-charcoal"
        aria-label="Property options"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => onOpenChange(!open)}
      >
        <MoreVertical className="h-5 w-5" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[10rem] overflow-hidden rounded-xl border border-border bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm text-navy transition hover:bg-surface"
            onClick={onEditNote}
          >
            {noteLabel}
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full px-3 py-2.5 text-left text-sm text-danger transition hover:bg-surface"
            onClick={onRemove}
          >
            Remove
          </button>
        </div>
      ) : null}
    </div>
  )
}

function RemovePropertyConfirmModal({
  open,
  item,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean
  item: CollectionItem | null
  busy: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const { street } = splitAddressDisplay(item?.fullAddress ?? '')

  return (
    <FadeModal open={open} onClose={onClose} labelledBy="remove-property-title" className="z-[80]">
      <div className="rounded-3xl bg-white p-6 shadow-2xl">
        <h2 id="remove-property-title" className="text-lg font-bold text-navy">
          Remove property?
        </h2>
        <p className="mt-2 text-sm text-muted">
          {street
            ? `Remove “${street}” from this collection? You can add it again later.`
            : 'Remove this property from the collection? You can add it again later.'}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="danger" disabled={busy} onClick={onConfirm}>
            {busy ? <Spinner className="border-white border-t-transparent" /> : null}
            Remove
          </Button>
        </div>
      </div>
    </FadeModal>
  )
}

function EditCollectionModal({
  open,
  onClose,
  collection,
  uid,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  collection: PropertyCollection
  uid: string
  onSaved: (c: PropertyCollection) => void
}) {
  const [name, setName] = useState(collection.name)
  const [description, setDescription] = useState(collection.description)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setName(collection.name)
    setDescription(collection.description)
    setError(null)
  }, [open, collection])

  async function onSave() {
    setBusy(true)
    setError(null)
    try {
      await renameCollection(uid, collection.id, { name, description })
      onSaved({
        ...collection,
        name: name.trim(),
        nameLower: name.trim().toLowerCase(),
        description: description.trim(),
        updatedAt: new Date(),
      })
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FadeModal open={open} onClose={onClose} labelledBy="edit-collection-title" className="z-[80]">
      <div className="rounded-3xl bg-white p-6 shadow-2xl">
        <h2 id="edit-collection-title" className="text-lg font-bold text-navy">
          Edit collection
        </h2>
        <div className="mt-4 space-y-3">
          <div className="space-y-2">
            <Label htmlFor="edit-col-name">Name</Label>
            <Input
              id="edit-col-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-col-desc">Description</Label>
            <Textarea
              id="edit-col-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={280}
              className="min-h-[80px]"
            />
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <div className="flex justify-between gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={busy || !name.trim()}
              onClick={() => void onSave()}
            >
              {busy ? <Spinner className="border-white border-t-transparent" /> : null}
              Save
            </Button>
          </div>
        </div>
      </div>
    </FadeModal>
  )
}

function EditNoteModal({
  open,
  item,
  onClose,
  uid,
  collectionId,
  onSaved,
}: {
  open: boolean
  item: CollectionItem | null
  onClose: () => void
  uid: string
  collectionId: string
  onSaved: (propertyId: string, note: string) => void
}) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !item) return
    setNote(item.note)
    setError(null)
  }, [open, item])

  async function onSave() {
    if (!item) return
    setBusy(true)
    setError(null)
    try {
      const next = note.slice(0, NOTE_MAX)
      await updateCollectionItemNote(uid, collectionId, item.propertyId, next)
      onSaved(item.propertyId, next)
    } catch (e) {
      setError(callableErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <FadeModal open={open} onClose={onClose} labelledBy="edit-note-title" className="z-[80]">
      <div className="rounded-3xl bg-white p-6 shadow-2xl">
        <h2 id="edit-note-title" className="text-lg font-bold text-navy">
          Property note
        </h2>
        <p className="mt-1 text-sm text-muted">Optional — only you can see this.</p>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={NOTE_MAX}
          className="mt-4 min-h-[120px]"
          placeholder="Add a private note…"
        />
        {error ? <p className="mt-2 text-sm text-danger">{error}</p> : null}
        <div className="mt-4 flex justify-between gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" disabled={busy} onClick={() => void onSave()}>
            {busy ? <Spinner className="border-white border-t-transparent" /> : null}
            Save note
          </Button>
        </div>
      </div>
    </FadeModal>
  )
}
